import { useState } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { useAssignableUsers } from '@/services/users';
import { useAssignVedlikehold } from '@/services/vedlikehold';
import { nb } from '@/i18n/nb';
import type { VedlikeholdEntry } from '@/types/api';
import s from './Vedlikehold.module.css';

/*
 * AssignPickerModal — pick the responsible user for a Dugnadsoppgave (per the
 * dugnad-assign-picker wireframe). A single-select radio list over ALL users (the
 * minimal /users/assignable endpoint), pre-selecting the current assignee so this
 * doubles as the reassign UI. Confirming assigns → status `tildelt`.
 *
 * Clearing the assignee (→ åpen) is deliberately NOT here — that lives on the status
 * control's «Åpen» option (ADR-0028). Open to any logged-in user; the server does the
 * real authz (no ownership check on this path).
 */
export function AssignPickerModal({
  task,
  onClose,
}: {
  task: VedlikeholdEntry;
  onClose: () => void;
}) {
  const { data: users, isLoading, isError } = useAssignableUsers();
  const assignMutation = useAssignVedlikehold();
  const [selected, setSelected] = useState<string | null>(task.assigneeUserId);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function handleSubmit() {
    if (selected === null) return;
    setSubmitError(null);
    assignMutation.mutate(
      { id: task.id, assigneeUserId: selected },
      {
        onSuccess: () => onClose(),
        onError: (e) =>
          setSubmitError(e instanceof Error ? e.message : nb.vedlikehold.assignModal.errorTitle),
      },
    );
  }

  return (
    <Modal open={true} onClose={onClose} title={nb.vedlikehold.assignModal.title}>
      <p className={s.modalHelp}>{nb.vedlikehold.assignModal.help}</p>

      {isLoading && <p className={s.status} aria-live="polite" />}
      {isError && <p className={s.status}>{nb.vedlikehold.assignModal.loadError}</p>}
      {users && users.length === 0 && <p className={s.empty}>{nb.vedlikehold.assignModal.empty}</p>}

      {users && users.length > 0 && (
        <fieldset className={s.pickerList}>
          <legend className="sr-only">{nb.vedlikehold.assignModal.title}</legend>
          {users.map((u) => (
            <label key={u.id} className={s.pickerOption}>
              <input
                type="radio"
                name="dugnad-assignee"
                value={u.id}
                checked={selected === u.id}
                onChange={() => setSelected(u.id)}
              />
              <span className={s.pickerName}>
                {u.displayName}
                {u.id === task.assigneeUserId && (
                  <span className={s.pickerCurrent}> {nb.vedlikehold.assignModal.currentHint}</span>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {submitError && (
        <p role="alert" className={s.submitError}>
          {submitError}
        </p>
      )}

      <div className={s.modalActions}>
        <Button variant="secondary" onClick={onClose}>
          {nb.common.cancel}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={assignMutation.isPending}
          disabled={selected === null}
        >
          {nb.vedlikehold.assignModal.submit}
        </Button>
      </div>
    </Modal>
  );
}
